<template>
  <Teleport to="body">
    <cl-modal
      :close-modal-text="$t('close')"
      :visible="visible"
      :size="'md'"
      test-id="update-system-modal"
      @on-close="onClose"
    >
      <template #[`header.title`]>
        <cl-heading :size="'h4'">{{ modalTitle }}</cl-heading>
      </template>
      <div>
        <section class="tw-mt-6 tw-mb-10">
          <cl-stepper :steps="steps" />
        </section>
        <!-- edit system -->
        <section v-if="systemStep.current">
          <div class="tw-grid tw-grid-cols-1 tw-gap-5 max-md:tw-grid-cols-1">
            <div class="tw-col-span-1 tw-mb-2">
              <cl-form-group>
                <cl-form-label
                  label-for="name"
                  class="hover:tw-cursor-pointer tw-mr-4"
                >
                  {{ $t("name") }}
                </cl-form-label>
                <cl-form-input
                  :id="'name'"
                  v-model:value="form.systemForm.name"
                  :name="'name'"
                  :type="'text'"
                  :placeholder="$t('system-name')"
                  :state="
                    !v$.systemForm.name.$dirty
                      ? undefined
                      : !v$.systemForm.name.$error
                  "
                  :data-test-id="'edit-system-input-name'"
                  @blur="handleBlur('systemForm', 'name')"
                  @input="v$.systemForm.name.$reset()"
                />
                <span v-if="!v$.systemForm.name.$error" class="tw-text-sm">
                  {{ $t("customer-name-hint") }}
                </span>
                <span
                  v-if="v$.systemForm.name.$error"
                  class="tw-text-system-error-500 tw-text-sm"
                >
                  <span v-if="!v$.systemForm.name.required.$response">
                    {{ $t("required-field") }}
                  </span>
                  <span
                    v-else-if="
                      !v$.systemForm.name.maxLength.$response ||
                      !v$.systemForm.name.minLength.$response
                    "
                  >
                    {{ $t("customer-name-validation") }}
                  </span>
                </span>
              </cl-form-group>
            </div>
          </div>
          <div class="tw-grid tw-grid-cols-1 tw-gap-5 max-md:tw-grid-cols-1">
            <div class="tw-col-span-1 tw-mb-2">
              <cl-form-group>
                <cl-form-label
                  label-for="description"
                  class="hover:tw-cursor-pointer tw-mr-4"
                >
                  {{ $t("description-label") }}
                </cl-form-label>
                <cl-form-input
                  :id="'description'"
                  v-model:value="v$.systemForm.description.$model"
                  :name="'description'"
                  :type="'text'"
                  :placeholder="$t('description-placeholder')"
                  :data-test-id="'edit-system-input-description'"
                  :state="
                    !v$.systemForm.description.$dirty
                      ? undefined
                      : !v$.systemForm.description.$error
                  "
                />
                <span
                  v-if="v$.systemForm.description.$error"
                  class="tw-text-system-error-500 tw-text-sm"
                >
                  <span v-if="!v$.systemForm.description.maxLength.$response">
                    {{ $t("description-255-characters") }}
                  </span>
                </span>
              </cl-form-group>
            </div>
          </div>
        </section>
      </div>
      <!-- edit Solutions step 2-->
      <section v-if="solutionsStep.current">
        <!-- email security -->
        <p
          v-dompurify-html="
            $t('select-solutions-msp', {
              CustomerName: form.systemForm.name,
            })
          "
          class="tw-mb-4"
        ></p>
        <section class="">
          <div class="tw-flex tw-flex-row tw-justify-start">
            <div
              v-tippy="{
                content: t('system-solutions-disabled'),
                placement: 'top',
              }"
            >
              <cl-toggle
                id="emailSecurity.switcher"
                name="emailSecurity-switcher"
                class="tw-mr-3 tw-mb-2"
                :data-test-id="`edit-system-email-security-toggle`"
                :value="form.emailSecurity.switcher"
                disabled
              />
            </div>
            <span>
              {{ $t("email-security-solution") }}
            </span>
          </div>
          <div
            v-if="form.emailSecurity.switcher"
            class="tw-grid tw-grid-cols-2 tw-gap-5"
          >
            <div class="w-full w-2/4 px-2 mb-2 md:mb-0 tw-col-span-1">
              <cl-form-group>
                <cl-form-label label-for="seats_purchased">
                  {{ $t("licenses") }}
                </cl-form-label>
                <cl-form-input
                  :id="'seats_purchased'"
                  v-model:value="form.emailSecurity.seats_purchased"
                  :name="'seats_purchased'"
                  :type="'number'"
                  :placeholder="'Licenses'"
                  :data-test-id="`edit-system-email-security-licenses`"
                  :min="0"
                  :max="100000"
                  disabled
                />
              </cl-form-group>
            </div>
          </div>
        </section>

        <!-- phishing simulation -->
        <section class="tw-mb-5 tw-mt-3">
          <div class="tw-flex tw-flex-row tw-justify-start">
            <div
              v-tippy="{
                content: t('system-solutions-disabled'),
                placement: 'top',
              }"
            >
              <cl-toggle
                id="phishingSimulation-switcher"
                name="phishingSimulation-switcher"
                class="tw-mr-3"
                :data-test-id="`edit-system-phishing-simulation-toggle`"
                :value="form.phishingSimulation.switcher"
                disabled
              />
            </div>
            <span>
              {{ $t("phishing-simulation-label") }}
            </span>
          </div>
          <div
            v-if="form.phishingSimulation.switcher"
            class="tw-grid tw-grid-cols-2 tw-gap-5"
          >
            <div class="w-full w-2/4 px-2 mb-2 md:mb-0 tw-col-span-1">
              <cl-form-group>
                <cl-form-label label-for="licenses">
                  {{ $t("licenses") }}
                </cl-form-label>
                <cl-form-input
                  :id="'licenses'"
                  v-model:value="form.phishingSimulation.seats_purchased"
                  :name="'licenses'"
                  :type="'number'"
                  :placeholder="'Licenses'"
                  :data-test-id="`edit-system-phishing-simulation-licenses`"
                  :min="0"
                  :max="100000"
                  disabled
                />
              </cl-form-group>
            </div>
          </div>
        </section>
        <!-- security awareness training -->
        <section class="tw-mb-5 tw-mt-3">
          <div class="tw-flex tw-flex-row tw-justify-start">
            <div
              v-tippy="{
                content: t('system-solutions-disabled'),
                placement: 'top',
              }"
            >
              <cl-toggle
                id="sat-switcher"
                name="sat-switcher"
                class="tw-mr-3"
                :data-test-id="`edit-system-sat-toggle`"
                :value="form.sat.switcher"
                disabled
              />
            </div>
            <span>
              {{ $t("sat-label") }}
            </span>
          </div>
          <div v-if="form.sat.switcher" class="tw-grid tw-grid-cols-2 tw-gap-5">
            <div class="w-full w-2/4 px-2 mb-2 md:mb-0 tw-col-span-1">
              <cl-form-group>
                <cl-form-label label-for="licenses">
                  {{ $t("licenses") }}
                </cl-form-label>
                <cl-form-input
                  :id="'licenses'"
                  v-model:value="form.sat.seats_purchased"
                  :name="'licenses'"
                  :type="'number'"
                  :placeholder="'Licenses'"
                  :data-test-id="`edit-system-sat-licenses`"
                  :min="0"
                  :max="100000"
                  disabled
                />
              </cl-form-group>
            </div>
          </div>
        </section>
        <!-- email archiving -->
        <section class="tw-mb-5 tw-mt-3">
          <div class="tw-flex tw-flex-row tw-justify-start">
            <div
              v-tippy="{
                content: t('system-solutions-disabled'),
                placement: 'top',
              }"
            >
              <cl-toggle
                id="emailArchiving-switcher"
                name="emailArchiving-switcher"
                class="tw-mr-3"
                :data-test-id="`edit-system-email-archiving-toggle`"
                :value="form.emailArchiving.switcher"
                disabled
              />
            </div>
            <span>
              {{ $t("email-archiving-label") }}
            </span>
          </div>
          <div
            v-if="form.emailArchiving.switcher"
            class="tw-grid tw-grid-cols-2 tw-gap-5"
          >
            <div class="w-full w-2/4 px-2 mb-2 md:mb-0 tw-col-span-1">
              <cl-form-group>
                <cl-form-label label-for="licenses">
                  {{ $t("licenses") }}
                </cl-form-label>
                <cl-form-input
                  :id="'licenses'"
                  v-model:value="form.emailArchiving.seats_purchased"
                  :name="'licenses'"
                  :type="'number'"
                  :placeholder="'Licenses'"
                  :data-test-id="`edit-system-email-archiving-licenses`"
                  :min="0"
                  :max="100000"
                  disabled
                />
              </cl-form-group>
            </div>
          </div>
        </section>
        <cl-callout
          :close-text="$t('close')"
          id="system-solutions-disabled"
          :variant="'info'"
        >
          <template #message>
            {{ t("system-solutions-disabled") }}
          </template>
        </cl-callout>
      </section>
      <!-- footer -->
      <template #[`footer.decline`]>
        <cl-button
          data-test-id="update-system-btn-cancel"
          :variant="'secondary-ghost'"
          @on-click="onClose"
        >
          <span>{{ t("cancel") }}</span>
        </cl-button>
        <template v-if="solutionsStep.current">
          <cl-button
            data-test-id="edit-system-input-btn-go-back-to-admin-step"
            :variant="'secondary-outline'"
            @on-click="goToStep(1)"
          >
            <span>{{ t("back") }}</span>
          </cl-button>
        </template>
      </template>
      <template #[`footer.accept`]>
        <template v-if="systemStep.current">
          <cl-button
            variant="secondary"
            data-test-id="edit-system-btn-go-to-customers-step"
            :disabled="v$.systemForm.$invalid"
            @on-click="
              () => {
                systemStep.completed = true;
                goToStep(2);
              }
            "
          >
            <span>{{ t("next") }}</span>
          </cl-button>
        </template>
        <template v-if="solutionsStep.current">
          <cl-button v-if="updatingSystem" :variant="'secondary'" disabled>
            <cl-spinner
              :loading-text="$t('loading')"
              :size="'small'"
              class="mr-2"
            />
            <span>Saving...</span>
          </cl-button>
          <cl-button
            v-else
            variant="secondary"
            data-test-id="create-save-system-btn-save-save"
            @on-click="updateSystem"
          >
            <span>{{ t("save-changes") }}</span>
          </cl-button>
        </template>
      </template>
    </cl-modal>
  </Teleport>
</template>
<script setup lang="ts">
import { computed, reactive, ref, toRefs, watch } from "vue";
import type { Step } from "@titanhq/frontend-component-library/dist/types/src/components/stepper/types";
import { useOrganisationsStore } from "@/store/organisations.store";
import { TIER } from "@/constants";
import {
  ARC_SERVICE,
  PHISH_SERVICE,
  PHISHING_SIMULATION,
  SAT_SERVICE,
} from "@/utils/services";
import { useVuelidate } from "@vuelidate/core";
import { maxLength, minLength, required } from "@vuelidate/validators";

import { useI18n } from "vue-i18n";
import { Organisation } from "@/types/organisatio";
import {
  LicenceWithSeats,
  ResellerOrganisationResponse,
} from "@/types/licenceSolutions.ts";
import { useToastStore } from "@/components/TToasts/toast.store.ts";

const toastStore = useToastStore();

interface TUpdateSystemsProps {
  visible: boolean;
  orginisationObject: Organisation;
  listAllLicences: ResellerOrganisationResponse;
}

const props = defineProps<TUpdateSystemsProps>();
const { visible, orginisationObject, listAllLicences } = toRefs(props);

const { t } = useI18n();
const organisationsStore = useOrganisationsStore();

const modalTitle = computed(() => t("edit-system"));
const stepLabel = computed(() => t("edit-system"));

const isServiceAvailable = (service_uuid: string) => {
  const service = listAllLicences.value?.licences?.find(
    (obj: LicenceWithSeats) => obj.solution_uuid === service_uuid,
  );
  return service?.seats_purchased ?? 0;
};

const hasServiceAvailable = (service_uuid: string) => {
  return listAllLicences.value.licences.some(
    (obj: LicenceWithSeats) => obj.solution_uuid === service_uuid || false,
  );
};

const form = reactive({
  systemForm: {
    name: "",
    description: "",
    uuid: "",
  },
  emailSecurity: {
    seats_purchased: isServiceAvailable(PHISH_SERVICE) || "",
    switcher: hasServiceAvailable(PHISH_SERVICE),
  },
  emailArchiving: {
    seats_purchased: isServiceAvailable(ARC_SERVICE) || "",
    switcher: hasServiceAvailable(ARC_SERVICE),
  },
  phishingSimulation: {
    seats_purchased: isServiceAvailable(PHISHING_SIMULATION) || "",
    switcher: hasServiceAvailable(PHISHING_SIMULATION),
  },
  sat: {
    seats_purchased: isServiceAvailable(SAT_SERVICE) || "",
    switcher: hasServiceAvailable(SAT_SERVICE),
  },
});
const rules = computed(() => ({
  systemForm: {
    name: { required, minLength: minLength(3), maxLength: maxLength(100) },
    description: { maxLength: maxLength(100) },
  },
}));
const v$ = useVuelidate(rules, form);

const systemStep = ref<Step>({
  index: 1,
  label: stepLabel.value,
  current: true,
  completed: false,
  actionable: false,
});

const solutionsStep = ref<Step>({
  index: 2,
  label: t("step-edit-solutions"),
  current: false,
  completed: false,
  actionable: false,
});

const steps = computed<Step[]>(() => [systemStep.value, solutionsStep.value]);
const stepsRefs = [systemStep, solutionsStep];

const goToStep = (index: number | string) => {
  for (const step of stepsRefs) {
    if (index === step.value.index) {
      step.value.current = true;
      step.value.completed = false;

      continue;
    }

    step.value.current = false;
  }
};

const handleBlur = async (form: string, field: string) => {
  const isValid = await v$.value[form][field].$validate();
  if (!isValid) {
    return;
  }
};

const updatingSystem = ref(false);

const updateSystem = async () => {
  try {
    updatingSystem.value = true;
    await updateOrganisation();
  } finally {
    await finished();
  }
};

const finished = async () => {
  updatingSystem.value = false;
  emit("on-update");

  toastStore.displayToast({
    title: t("success"),
    message: t("system-with-name-updated-successfully", {
      CustomerName: form.systemForm.name,
    }),
    duration: 5000,
    variant: "success",
    testId: "update-system-toast-success",
  });
};
const updateOrganisation = async () => {
  try {
    await organisationsStore.updateOrg({
      formData: form.systemForm,
      parent_uuid: null,
      organisation_type: TIER.system,
    });
  } catch {
    updatingSystem.value = false;
  }
};

const emit = defineEmits<{
  (event: "on-close"): void;
  (event: "on-update"): void;
}>();
const onClose = () => {
  emit("on-close");
};

watch(
  () => orginisationObject.value,
  (organisation) => {
    if (organisation) {
      form.systemForm.name = organisation.name || "";
      form.systemForm.description = organisation.description || "";
      form.systemForm.uuid = organisation.uuid || "";
    }
  },
  { immediate: true },
);
</script>
